import Phaser from 'phaser';

/**
 * Tiny per-scene helper that translates a USB / Bluetooth gamepad on the
 * W3C Standard Gamepad mapping into menu-navigation callbacks.
 *
 * Re-fires only on rising edges - for D-pad and face buttons that means
 * "press → release → press"; for the analog stick it means "stick must
 * cross back through the rest band before firing the next direction".
 * The contract holds across screen changes too: anything already held at
 * the moment a nav attaches is ignored until it is released once, so a
 * button mashed on the previous screen cannot fire on this one.
 *
 * Wire it up in a scene's `create()`:
 *   this.nav = new GamepadNav(this, { onUp, onDown, onConfirm, ... });
 * and tick it from `update()`:
 *   this.nav?.update();
 */

export interface GamepadNavCallbacks {
  onUp?: () => void;
  onDown?: () => void;
  onLeft?: () => void;
  onRight?: () => void;
  onConfirm?: () => void; // A / Cross  (button 0). Also B / Circle (button 1) as courtesy alias.
  onCancel?: () => void;  // B / Circle (button 1) - only fires when no onConfirm consumer is set.
                           // If both are set, A=confirm, B=cancel. If ONLY onConfirm is set, all
                           // four face buttons (A, B, X, Y / buttons 0-3) fire it, so a player on
                           // any non-standard gamepad mapping can press "the obvious button" and
                           // press the focused pill.
  onPause?: () => void;   // Start (9) or Select/Back (8).
}

const DEADZONE = 0.55;        // Stick must exceed this to count as a press.
const REST_BAND = 0.30;       // Stick must drop below this to "re-arm" the next press.

export class GamepadNav {
  private pad?: Phaser.Input.Gamepad.Gamepad;
  private prevStickX: 'left' | 'right' | 'rest' = 'rest';
  private prevStickY: 'up' | 'down' | 'rest' = 'rest';
  private prevButtons: Record<number, boolean> = {};
  private detach: () => void;

  constructor(scene: Phaser.Scene, private cb: GamepadNavCallbacks) {
    const gp = scene.input.gamepad;
    if (!gp) {
      this.detach = () => {};
      return;
    }

    const adopt = (pad: Phaser.Input.Gamepad.Gamepad) => {
      this.pad = pad;
      // Seed edge detection from the pad's CURRENT physical state, so any
      // input already held when this nav attaches (a button still down from
      // mashing on the previous screen, or a pad plugged in mid-press) must
      // be RELEASED and pressed again before it fires. Seeding a blank
      // state here made every held input read as a fresh rising edge on the
      // first update, which let one held button blind-dismiss a narrative
      // card the same frame it appeared.
      const ax = pad.axes.length > 0 ? pad.axes[0].getValue() : 0;
      const ay = pad.axes.length > 1 ? pad.axes[1].getValue() : 0;
      this.prevStickX = ax < -DEADZONE ? 'left' : (ax > DEADZONE ? 'right' : 'rest');
      this.prevStickY = ay < -DEADZONE ? 'up' : (ay > DEADZONE ? 'down' : 'rest');
      this.prevButtons = {};
      pad.buttons.forEach((b, i) => { this.prevButtons[i] = !!(b && b.pressed); });
    };
    const drop = (pad: Phaser.Input.Gamepad.Gamepad) => {
      if (this.pad === pad) {
        this.pad = undefined;
        // Neutralise any latched stick state so the next update() does not
        // emit a phantom direction event from the disconnected pad's last
        // axis reading. Without this, a player who walks off mid-press can
        // make the cursor jump or auto-select a CTA when the pad is plugged
        // back in.
        this.prevStickX = 'rest';
        this.prevStickY = 'rest';
        this.prevButtons = {};
      }
    };

    const existing = gp.getPad(0);
    if (existing) adopt(existing);
    gp.on('connected', adopt);
    gp.on('disconnected', drop);

    const cleanup = () => {
      gp.off('connected', adopt);
      gp.off('disconnected', drop);
    };
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, cleanup);
    scene.events.once(Phaser.Scenes.Events.DESTROY, cleanup);
    this.detach = cleanup;
  }

  update(): void {
    const pad = this.pad;
    if (!pad) return;

    // --- Horizontal: left stick (axis 0) OR D-pad ---
    const ax = pad.axes.length > 0 ? pad.axes[0].getValue() : 0;
    let stickX: 'left' | 'right' | 'rest';
    if (ax < -DEADZONE) stickX = 'left';
    else if (ax > DEADZONE) stickX = 'right';
    else if (Math.abs(ax) < REST_BAND) stickX = 'rest';
    else stickX = this.prevStickX; // hysteresis band - keep prior state

    if (stickX !== this.prevStickX) {
      if (stickX === 'left' && this.cb.onLeft) this.cb.onLeft();
      if (stickX === 'right' && this.cb.onRight) this.cb.onRight();
      this.prevStickX = stickX;
    }
    this.edge(14, () => this.cb.onLeft?.());   // D-pad left
    this.edge(15, () => this.cb.onRight?.());  // D-pad right

    // --- Vertical: left stick (axis 1) OR D-pad ---
    const ay = pad.axes.length > 1 ? pad.axes[1].getValue() : 0;
    let stickY: 'up' | 'down' | 'rest';
    if (ay < -DEADZONE) stickY = 'up';
    else if (ay > DEADZONE) stickY = 'down';
    else if (Math.abs(ay) < REST_BAND) stickY = 'rest';
    else stickY = this.prevStickY;

    if (stickY !== this.prevStickY) {
      if (stickY === 'up' && this.cb.onUp) this.cb.onUp();
      if (stickY === 'down' && this.cb.onDown) this.cb.onDown();
      this.prevStickY = stickY;
    }
    this.edge(12, () => this.cb.onUp?.());     // D-pad up
    this.edge(13, () => this.cb.onDown?.());   // D-pad down

    // --- Confirm / Cancel ---
    // If a scene wires only onConfirm (no onCancel), treat ALL four face buttons
    // (A/B/X/Y, indices 0-3) as confirm. Some gamepads report their two visible
    // face buttons on non-standard indices (e.g. Switch-style or generic USB
    // pads), and this guarantees that whichever button the player reaches for
    // presses the focused pill. With both wired, we keep the standard A=confirm,
    // B=cancel split so screens that want both behaviors can ship them.
    if (this.cb.onConfirm && this.cb.onCancel) {
      this.edge(0, this.cb.onConfirm);
      this.edge(1, this.cb.onCancel);
    } else if (this.cb.onConfirm) {
      this.edge(0, this.cb.onConfirm);
      this.edge(1, this.cb.onConfirm);
      this.edge(2, this.cb.onConfirm);
      this.edge(3, this.cb.onConfirm);
    } else if (this.cb.onCancel) {
      this.edge(1, this.cb.onCancel);
    }

    // --- Pause ---
    if (this.cb.onPause) {
      this.edge(8, this.cb.onPause);
      this.edge(9, this.cb.onPause);
    }
  }

  private edge(buttonIndex: number, fire: () => void): void {
    const held = this.pad?.buttons[buttonIndex]?.pressed ?? false;
    if (held && !this.prevButtons[buttonIndex]) fire();
    this.prevButtons[buttonIndex] = held;
  }

  /**
   * True if a gamepad is currently connected. Lets callers light up a
   * selection halo on a lone CTA when the player is on a joystick (and skip
   * it for mouse / keyboard sessions). Updates live with connect / disconnect.
   */
  isPadConnected(): boolean {
    return !!this.pad;
  }

  destroy(): void {
    this.detach();
    this.pad = undefined;
  }
}
